import { map, Observable } from 'rxjs';
import { Component, OnInit, OnDestroy, ViewChild, ComponentFactoryResolver, AfterViewInit, ViewContainerRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { DataGridColumn, DataGridEventInformation } from '../datagrid/datagrid-core.component';
import { DataGridComponent } from '../datagrid/datagrid.component';
import { CrudDataService } from '../../services/crud-data.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';

import { OrderFilterBase, OrderField, EnOrder } from '../../model/search/orderFilterBase';
import { ColumnInfoBase, ColumnInfo } from '../../model/search/columnInfoBase';
import { TransactionalInformation } from '../../model/generic/transactionalInformation';
import { EntityBase } from '../../model/generic/entityBase';
import { SearchFilterBase } from '../../model/search/searchFilterBase';
import { FileInfo } from '../../model/generic/fileInfo';
import { encode } from 'base64-arraybuffer';

@Component({
    templateUrl: './browse.component.html',
    styleUrls: ['./browse.component.css'],
    standalone: false
})
export class BrowseComponent<T extends EntityBase> implements OnInit, AfterViewInit, OnDestroy {

  //#region Properties
  @ViewChild(DataGridComponent) grid: DataGridComponent;
  @ViewChild('browseFilter', { static: true, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  @ViewChild('modalConfirmDelete') modalConfirmDelete: any;
  @ViewChild('modalUploadFicheiro') modalUploadFicheiro: any;
  @ViewChild('modalAcompanhamento') modalAcompanhamento: any;
  @ViewChild('modalRegistoKms') modalRegistoKms: any;

  protected browseRouterLink: string;
  protected manageRouterLink: string;

  protected message: string;
  protected currentFile?: FileInfo;

  public filter: SearchFilterBase;

  public uploadCallback?: (id: any, descritivo: string, observacoes: string, file: FileInfo) => void;
  public downloadCallback?: (id: any) => void;
  public exportReportCallback?: (filter: any) => Observable<any>;
  public exportAnalysisCallBack?: (filter: any) => Observable<any>;
  public acompanhamentoCallback?: (id: any) => Observable<any>;
  public registoKmsCallback?: (id: number, kms: number) => Observable<any>;

  public canDelete: boolean | true;
  public canDeleteHandler: any = null;
  public exportReportLabel: string;
  public showExportReport: boolean | false;
  public isExportReport: boolean | false;
  public showExportExcel: boolean | false;
  public isExportingExcel: boolean | false;
  public exportAnalysisLabel: string;
  public showExportAnalysis: boolean | false;
  public isExportingAnalysis: boolean | false;
  public showAdd: boolean | true;

  public globalMessage: string;
  public title: string;

  public popupListData: any[];
  public popupDescription: string;
  public popupRemarks: string;
  public popupKms: number;
  public popupSelectedItem: number;
  public popupRegistoOk: boolean;
  public gridData: T[] = [];
  public columns: Array<DataGridColumn> = [];

  public currentPageNumber: number;
  public totalRows: number;
  public totalPages: number;
  public pageSize: number;

  public autoFilter: boolean;
  public delaySearch: boolean;
  public runningSearch: boolean;
  //#endregion

  //#region Instance Members
  private sortDirection: string;
  private sortExpression: string;

  private subSearch: any;
  private subQueryParam: any;

  private searchAndGo: boolean | false;

  protected componentFilter: any;
  //#endregion

  constructor(
    protected componentFactoryResolver: ComponentFactoryResolver,
    protected router: Router, protected route: ActivatedRoute, protected modalService: NgbModal,
    protected dataService: CrudDataService<T>) {

    this.currentPageNumber = 1;
    this.autoFilter = false;
    this.totalPages = 0;
    this.totalRows = 0;
    this.pageSize = 10;
    this.sortDirection = 'ASC';
    this.sortExpression = '';
  }

  ngOnInit(): void {
    this.loadComponent();
  }

  ngAfterViewInit() {
    this.subQueryParam = this.route.queryParams.subscribe(queryParams => {
      if (queryParams) {
        const s = queryParams.s; // autoSearch
        if (s === '1') {
          // console.log('autoFilter: TRUE');
          this.autoFilter = true;
        } else {
          const sg = queryParams.sg;
          if (sg === '1') { // autoSearch and go if result is one record
            this.autoFilter = true;
            this.searchAndGo = true;
            // console.log('searchAndGo: TRUE');
          }
        }
      }
      if (this.autoFilter) { // do autoSearch as soon has possible, but aftert inicialization
        setTimeout(() => {
          this.search();
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subSearch) { this.subSearch.unsubscribe(); }
    if (this.subQueryParam) { this.subQueryParam.unsubscribe(); }
  }

  //#region Public Methods
  public reset(): void {
    this.currentPageNumber = 1;
    this.delaySearch = false;
    this.executeSearch();
  }

  public addNew = (event: any): void => {
    this.router.navigateByUrl(this.manageRouterLink + '/new');
  }

  public exportExcel = (event: any): void => {
    this.executeSearchExcel();
  }

  public exportReport = (event: any): void => {
    if (this.exportReportCallback) {
      this.isExportReport = true;
      this
        .exportReportCallback(this.filter)
        .subscribe((response: { filename: string, content: BlobPart; }) => {
          if (response) {
            const b = new Blob([response.content], { type: 'application/vnd.ms-excel' });
            saveAs(b, response.filename);
          }

          this.isExportReport = false;
        },
          (error: any) => {
            console.log(error);
            this.isExportReport = false;
          });
    }
  }

  public exportAnalysis = (event: any): void => {
    if (this.exportAnalysisCallBack) {
      this.isExportingAnalysis = true;
      this
        .exportAnalysisCallBack(this.filter)
        .subscribe((response: { filename: string, content: BlobPart; }) => {
          if (response) {
            const b = new Blob([response.content], { type: 'application/vnd.ms-excel' });
            saveAs(b, response.filename);
          }
          this.isExportingAnalysis = false;
        },
          (error: any) => {
            console.log(error);
            this.isExportingAnalysis = false;
          });
    }
  }

  public search(): void {
    this.currentPageNumber = this.filter.pageNumber ?? 1;
    this.delaySearch = false;
    this.executeSearch();
  }

  public datagridEvent = (event: any): void => {
    const datagridEvent: DataGridEventInformation = event.value;

    if (datagridEvent.EventType === 'PagingEvent') {
      this.paging(datagridEvent.CurrentPageNumber);
    } else if (datagridEvent.EventType === 'PageSizeChanged') {
      this.pageSizeChanged(datagridEvent.PageSize);
    } else if (datagridEvent.EventType === 'ItemSelected') {
      this.selectedItem(datagridEvent.ItemSelected);
    } else if (datagridEvent.EventType === 'Sorting') {
      this.sort(datagridEvent.SortDirection, datagridEvent.SortExpression);
    } else if (datagridEvent.EventType === 'ButtonClicked') {
      this.buttonClicked(datagridEvent.ButtonClicked, datagridEvent.ItemSelected, datagridEvent.CallBack);
    }
  }

  public selectFile = (event: any): void => {
    const file = event.target.files[0];
    const fi = new FileInfo();
    const fileReader = new FileReader();
    fileReader.onloadend = (e) => {
      const bytes = fileReader.result as ArrayBuffer;
      fi.contentArrayBufferBase64 = encode(bytes);
      fi.filename = file.name;
    };
    fileReader.readAsArrayBuffer(file);
    this.currentFile = fi;
  }

  public upload = (event: any): void => {
    const row = this.gridData[this.popupSelectedItem];
    const rowId = row[row.keyProperty];
    if (this.uploadCallback && this.currentFile) {
      this.uploadCallback(rowId, this.popupDescription, this.popupRemarks, this.currentFile);
    }
  }

  public registaKms = (event: any): void => {
    const row = this.gridData[this.popupSelectedItem];
    const rowId = row[row.keyProperty];
    if (this.registoKmsCallback && this.popupKms) {
      this
        .registoKmsCallback(rowId, this.popupKms)
        .subscribe((response) => {
          if (response && response !== '') {
            this.message = response;
            this.popupRegistoOk = false;
          } else {
            this.message = 'Registo enviado com sucesso.';
            this.popupRegistoOk = true;
          }
        },
          (error: any) => {
            console.log(error);
          });
    }
  }
  //#endregion

  //#region Private Methods
  private loadComponent() {
    if (this.componentFilter) {
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.componentFilter);
      if (this.viewContainerRef !== undefined) {
        const viewContainerRef = this.viewContainerRef;
        viewContainerRef.clear();

        const componentRef = viewContainerRef.createComponent(componentFactory);
        (componentRef.instance as any).filter = this.filter;
      }
    }
  }

  private buttonClicked = (buttonClicked: string, itemSelected: number, callBack?: (buttonName: string, row: any) => void): void => {
    // Delete de registo
    if (buttonClicked === 'X') {
      const row = this.gridData[itemSelected];
      const rowId = row[row.keyProperty];

      // ask permittion to the user
      this.modalService.open(this.modalConfirmDelete).result.then((result) => {
        if (result === 'Confirm') {
          this.dataService
            .remove(rowId)
            .subscribe(response => {
              console.log('Removed with success.');
              this.executeSearch();
            },
              error => {
                console.log(error);
              },
              () => {
                if (callBack) {
                  callBack(buttonClicked, row);
                }
              });
        }
      });
    }

    // Edição de registo
    if (buttonClicked === 'edit') {
      this.selectedItem(itemSelected);
    }

    // Upload de ficheiro
    if (buttonClicked === 'upload') {
      const row = this.gridData[itemSelected];
      this.popupSelectedItem = itemSelected;
      this.modalService.open(this.modalUploadFicheiro)
        .result.then((resul) => {
          if (resul === 'Confirm') {
          }
        }).catch((error) => {
          this.message = error;
        })
        .finally(() => {
          if (callBack) {
            callBack(buttonClicked, row);
          }
        });
    }

    // Download de ficheiro (Pdf ou Excel)
    if (buttonClicked === 'downloadPdf' || buttonClicked === 'downloadExcel') {
      const fileType = buttonClicked === 'downloadPdf' ? 'pdf' : 'excel';
      const row = this.gridData[itemSelected];
      const rowId = row[row.keyProperty];

      this.dataService
        .httpGetFiles(rowId, fileType)
        .subscribe((response: any) => {
          console.log('Download de file(s).');
          response.forEach((file: FileInfo) => {
            saveAs(file.content, file.filename);
          });
        },
          (error: any) => {
            console.log(error);
          },
          () => {
            if (callBack) {
              callBack(buttonClicked, row);
            }
          });
    }

    // Popup de registo de kms atualizados
    if (buttonClicked === 'registoKms') {
      const row = this.gridData[itemSelected];
      this.popupSelectedItem = itemSelected;
      this.popupKms = null;
      this.popupRegistoOk = false;
      this.message = '';
      this.modalService.open(this.modalRegistoKms)
        .result.then((resul) => {
          if (resul === 'Confirm') {
          }
        }).catch((error) => {
          this.message = error;
        })
        .finally(() => {
          if (callBack) {
            callBack(buttonClicked, row);
          }
        });
    }

    // Informação de acompanhamento de processo
    if (buttonClicked === 'acompanhamento') {
      const row = this.gridData[itemSelected];
      const rowId = row[row.keyProperty];

      if (this.acompanhamentoCallback) {
        this
          .acompanhamentoCallback(rowId)
          .subscribe((response: any) => {
            this.popupListData = response;
            this.modalService
              .open(this.modalAcompanhamento, { size: 'lg', centered: true, backdrop: true }).result
              .then((resul) => {
              }).catch((error) => {
                this.message = error;
              })
              .finally(() => {
                if (callBack) {
                  callBack(buttonClicked, row);
                }
              });
          });
      }
    }
  }

  private selectedItem = (itemSelected: number): void => {
    const rowSelected = itemSelected;
    const row = this.gridData[rowSelected];
    const rowId = row[row.keyProperty];

    this.setQueryParams();
    // this.router.navigateByUrl(this.manageRouterLink + '/' + rowId);
    this.router.navigate([this.manageRouterLink + '/' + rowId], { queryParamsHandling: 'preserve' })
  }

  private sort(sortDirection: string, sortExpression: string) {
    this.sortDirection = sortDirection;
    this.sortExpression = sortExpression;
    this.currentPageNumber = 1;
    this.delaySearch = false;
    this.executeSearch();
  }

  private paging(currentPageNumber: number) {
    this.currentPageNumber = currentPageNumber;
    this.delaySearch = false;
    this.executeSearch();
  }

  private pageSizeChanged(pageSize: number) {
    this.pageSize = pageSize;
    this.currentPageNumber = 1;
    this.delaySearch = false;
    this.executeSearch();
  }

  private executeSearchExcel = (): void => {
    if (this.isExportingExcel === true) { return; }

    this.filter.pageNumber = this.currentPageNumber;
    this.filter.pageSize = this.pageSize;

    if (this.sortDirection && this.sortExpression) {
      this.filter.order = new OrderFilterBase();
      this.filter.order.orderFields = [];
      const orderField: OrderField = new OrderField();
      orderField.fieldname = this.sortExpression;
      if (this.sortDirection === 'ASC') {
        orderField.order = EnOrder.Asc;
      } else {
        orderField.order = EnOrder.Desc;
      }

      this.filter.order.orderFields.push(orderField);
    }

    // ColumnInfoBase
    this.filter.columnInfo = new ColumnInfoBase();
    this.filter.columnInfo.columnsInfo = [];
    this.filter.columnInfo.title = this.title;
    this.columns.forEach((col) => {
      if (col.name) { // exclui columns sem dataField
        const columnInfo: ColumnInfo = new ColumnInfo();
        columnInfo.dataPropertyName = col.name;
        columnInfo.caption = col.description;
        this.filter.columnInfo.columnsInfo.push(columnInfo);
      }
    });

    this.isExportingExcel = true;

    this.subSearch = this.dataService
      .SearchExcel(this.filter)
      .subscribe((response: { content: BlobPart; }) => {
        if (response) {
          // file Excel
          const b = new Blob([response.content], { type: 'application/vnd.ms-excel' });
          saveAs(b, this.title + '.xlsx');
        }

        this.isExportingExcel = false;
      });
  }

  private executeSearch = (): void => {
    if (this.runningSearch === true) { return; }

    this.filter.pageNumber = this.currentPageNumber;
    this.filter.pageSize = this.pageSize;

    if (this.sortDirection && this.sortExpression) {
      this.filter.order = new OrderFilterBase();
      this.filter.order.orderFields = [];
      const orderField: OrderField = new OrderField();
      orderField.fieldname = this.sortExpression;
      if (this.sortDirection === 'ASC') {
        orderField.order = EnOrder.Asc;
      } else {
        orderField.order = EnOrder.Desc;
      }

      this.filter.order.orderFields.push(orderField);
    }

    this.setQueryParams();
    this.runningSearch = true;

    this.subSearch = this.dataService
      .search(this.filter)
      .subscribe(response => {
        const tsInfo = new TransactionalInformation();
        tsInfo.currentPageNumber = (response as any).pageNumber;
        tsInfo.pageSize = this.pageSize;
        this.totalRows = tsInfo.totalRows = response ? (response as any).totalCount : 0;
        tsInfo.totalPages = Math.round(tsInfo.totalRows / tsInfo.pageSize + 0.499999999);
        tsInfo.sortDirection = this.sortDirection;
        tsInfo.sortExpression = this.sortExpression;

        this.gridData = (response as any).dataList;
        this.grid.databind(tsInfo);
        this.runningSearch = false;

        if (this.searchAndGo && (response as any).dataList.length === 1) {
          console.log('componente browser: searchAndGO ative');
          this.selectedItem(0);
          return;
        }
      }, (error) => {
        console.log(error);
        this.runningSearch = false;
      }, () => {
        this.runningSearch = false;
      });
  }

  private setQueryParams = (): void => {
    if (this.filter) {
      const filter = this.filter;
      const myQueryParams: any = {};
      Object.getOwnPropertyNames(this.filter)
        .forEach(
          (val, idx, array) => {
            const value = filter[val];
            if (value) {
              const type = value.constructor.name;
              myQueryParams[val] = value;
            }
          }
        );

      if (this.searchAndGo) {
        myQueryParams.sg = '1';
      } else if (this.autoFilter) {
        myQueryParams.s = '1';
      }
      // console.log('queryParams: ' + JSON.stringify(myQueryParams));
      this.router.navigate([], { queryParams: myQueryParams, relativeTo: this.route });
    }
  }
  //#endregion

}
